import { useQueryClient } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import { Button, Card, Field, TextInput } from '../components/ui';
import { ApiError, apiData } from '../lib/api';
import { toastError } from '../lib/feedback';
import { useSession, type AdminSession } from '../lib/session';

function Frame({ title, subtitle, children }: { title: string; subtitle: string; children: ReactNode }) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-bg px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand font-bold text-brand-fg">
            B
          </span>
          <div>
            <p className="text-sm font-semibold">{title}</p>
            <p className="text-xs text-fg-muted">{subtitle}</p>
          </div>
        </div>
        <Card>{children}</Card>
      </div>
    </main>
  );
}

export function LoginPage() {
  const { t } = useTranslation();
  const session = useSession();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needTotp, setNeedTotp] = useState(false);
  const [busy, setBusy] = useState(false);

  if (session.data?.stage === 'enroll') return <Navigate to="/enroll" replace />;
  if (session.data?.mustChangePassword) return <Navigate to="/password" replace />;
  if (session.data?.stage === 'full') return <Navigate to="/" replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await apiData<{ status: 'ok' | 'enrollment_required'; admin: AdminSession }>(
        '/admin/auth/login',
        {
          method: 'POST',
          body: { email, password, ...(totp.trim() ? { totp: totp.trim() } : {}) },
        },
      );
      queryClient.setQueryData(['me'], result.admin);
    } catch (error) {
      const details = error instanceof ApiError ? error.details : undefined;
      const totpRequired =
        typeof details === 'object' &&
        details !== null &&
        'totpRequired' in details &&
        (details as { totpRequired?: boolean }).totpRequired;
      if (totpRequired) setNeedTotp(true);
      toastError(error, t('errorTitle'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Frame title={t('brand')} subtitle={t('auth.subtitle')}>
      <h1 className="mb-4 text-xl font-semibold">{t('auth.title')}</h1>
      <form className="space-y-4" onSubmit={(event) => void submit(event)}>
        <Field label={t('auth.email')}>
          <TextInput
            data-testid="login-email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>
        <Field label={t('auth.password')}>
          <TextInput
            data-testid="login-password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>
        <Field label={t('auth.totp')} hint={needTotp ? t('auth.totpRequired') : t('auth.totpHint')}>
          <TextInput
            data-testid="login-totp"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={totp}
            onChange={(event) => setTotp(event.target.value)}
          />
        </Field>
        <Button data-testid="login-submit" type="submit" className="w-full" loading={busy}>
          {t('auth.submit')}
        </Button>
      </form>
    </Frame>
  );
}

function nextAfterEnroll(session: AdminSession): string {
  return session.mustChangePassword ? '/password' : '/';
}

export function EnrollPage() {
  const { t } = useTranslation();
  const session = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [secret, setSecret] = useState('');
  const [qr, setQr] = useState('');
  const [code, setCode] = useState('');
  const [backup, setBackup] = useState<string[] | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (session.data?.stage !== 'enroll' || secret) return;
    let cancelled = false;
    apiData<{ secret: string; otpauthUrl: string }>('/admin/auth/totp/enroll', { method: 'POST', body: {} })
      .then(async (result) => {
        if (cancelled) return;
        setSecret(result.secret);
        setQr(await QRCode.toDataURL(result.otpauthUrl, { margin: 1, width: 220 }));
      })
      .catch((error) => toastError(error, t('errorTitle')));
    return () => {
      cancelled = true;
    };
  }, [secret, session.data?.stage, t]);

  if (session.isLoading) return null;
  if (!session.data) return <Navigate to="/login" replace />;
  // Backup codes are shown once. Stay on this screen until the admin confirms they saved them,
  // even after the session has already moved to the full stage.
  if (session.data.stage === 'full' && !backup) return <Navigate to={nextAfterEnroll(session.data)} replace />;

  async function verify(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const result = await apiData<{ backupCodes: string[] }>('/admin/auth/totp/verify', {
        method: 'POST',
        body: { code },
      });
      setBackup(result.backupCodes);
      await queryClient.invalidateQueries({ queryKey: ['me'] });
    } catch (error) {
      toastError(error, t('errorTitle'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Frame title={t('brand')} subtitle={t('auth.enrollSubtitle')}>
      <h1 className="text-xl font-semibold">{t('auth.enrollTitle')}</h1>
      <p className="mt-2 text-sm text-fg-muted">{t('auth.enrollBody')}</p>
      {backup ? (
        <div className="mt-4 space-y-4">
          <p className="text-sm">{t('auth.backupBody')}</p>
          <ul className="grid grid-cols-2 gap-2 font-mono text-sm">
            {backup.map((item) => (
              <li key={item} className="rounded-lg bg-muted px-2 py-1">
                {item}
              </li>
            ))}
          </ul>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={saved} onChange={(event) => setSaved(event.target.checked)} />
            {t('auth.backupSaved')}
          </label>
          <Button
            data-testid="backup-continue"
            className="w-full"
            disabled={!saved}
            onClick={() => {
              if (session.data) navigate(nextAfterEnroll(session.data));
            }}
          >
            {t('auth.continue')}
          </Button>
        </div>
      ) : (
        <form className="mt-4 space-y-4" onSubmit={(event) => void verify(event)}>
          {qr ? <img src={qr} alt={t('auth.qrAlt')} className="mx-auto rounded-xl bg-white p-2" /> : null}
          <p className="break-all text-center font-mono text-xs text-fg-muted" data-testid="totp-secret">
            {secret}
          </p>
          <Field label={t('auth.code')}>
            <TextInput
              data-testid="totp-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </Field>
          <Button data-testid="totp-verify" type="submit" className="w-full" loading={busy}>
            {t('auth.verify')}
          </Button>
        </form>
      )}
    </Frame>
  );
}

export function PasswordPage() {
  const { t } = useTranslation();
  const session = useSession();
  const queryClient = useQueryClient();
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNext] = useState('');
  const [busy, setBusy] = useState(false);

  if (session.isLoading) return null;
  if (!session.data) return <Navigate to="/login" replace />;
  if (session.data.stage === 'enroll') return <Navigate to="/enroll" replace />;
  if (!session.data.mustChangePassword) return <Navigate to="/" replace />;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await apiData('/admin/auth/change-password', {
        method: 'POST',
        body: { currentPassword, newPassword },
      });
      await queryClient.invalidateQueries({ queryKey: ['me'] });
    } catch (error) {
      toastError(error, t('errorTitle'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Frame title={t('brand')} subtitle={t('auth.passwordSubtitle')}>
      <h1 className="mb-2 text-xl font-semibold">{t('auth.passwordTitle')}</h1>
      <p className="mb-4 text-sm text-fg-muted">{t('auth.passwordBody')}</p>
      <form className="space-y-4" onSubmit={(event) => void submit(event)}>
        <Field label={t('auth.currentPassword')}>
          <TextInput
            data-testid="password-current"
            type="password"
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(event) => setCurrent(event.target.value)}
          />
        </Field>
        <Field label={t('auth.newPassword')} hint={t('auth.passwordHint')}>
          <TextInput
            data-testid="password-new"
            type="password"
            autoComplete="new-password"
            minLength={12}
            required
            value={newPassword}
            onChange={(event) => setNext(event.target.value)}
          />
        </Field>
        <Button data-testid="password-submit" type="submit" className="w-full" loading={busy}>
          {t('auth.changePassword')}
        </Button>
      </form>
    </Frame>
  );
}
